import React, { useState, useMemo, useEffect } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO, differenceInDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Package, Plus, Search, Loader2, Edit2, Trash2, Wrench, AlertTriangle,
  TrendingDown, Eye, Calendar, MapPin, User, FileText, Filter
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion, AnimatePresence } from "framer-motion";

import AtivoForm, { categorias } from "../components/ativos/AtivoForm";
import ManutencaoForm from "../components/ativos/ManutencaoForm";
import RelatorioDepreciacao from "../components/ativos/RelatorioDepreciacao";
import Paginacao from "../components/shared/Paginacao";

const statusLabels = {
  ativo: { label: "Ativo", color: "bg-green-100 text-green-700" },
  em_manutencao: { label: "Em Manutenção", color: "bg-amber-100 text-amber-700" },
  inativo: { label: "Inativo", color: "bg-stone-100 text-stone-700" },
  vendido: { label: "Vendido", color: "bg-blue-100 text-blue-700" },
  descartado: { label: "Descartado", color: "bg-red-100 text-red-700" }
};

const categoriaLabels = {
  veiculo: { label: "Veículo", icon: "🚗" },
  maquina_agricola: { label: "Máquina Agrícola", icon: "🚜" },
  implemento: { label: "Implemento", icon: "🔧" },
  equipamento: { label: "Equipamento", icon: "⚙️" },
  edificacao: { label: "Edificação", icon: "🏠" },
  terreno: { label: "Terreno", icon: "🌾" },
  mobiliario: { label: "Mobiliário", icon: "🪑" },
  informatica: { label: "Informática", icon: "💻" },
  outro: { label: "Outro", icon: "📦" }
};

export default function Ativos() {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");

  const [activeTab, setActiveTab] = useState("inventario");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [manutencaoDialogOpen, setManutencaoDialogOpen] = useState(false);
  const [detalheDialogOpen, setDetalheDialogOpen] = useState(false);
  const [editingAtivo, setEditingAtivo] = useState(null);
  const [editingManutencao, setEditingManutencao] = useState(null);
  const [selectedAtivo, setSelectedAtivo] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("todas");
  const [filterStatus, setFilterStatus] = useState("todos");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [manutencaoPage, setManutencaoPage] = useState(1);
  const [manutencaoPageSize, setManutencaoPageSize] = useState(10);

  const queryClient = useQueryClient();

  const { data: ativos = [], isLoading } = useQuery({
    queryKey: ['ativos', propriedadeId],
    queryFn: () => propriedadeId ? agrogestao.entities.AtivoImobilizado.filter({ propriedade_id: propriedadeId }) : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: manutencoes = [] } = useQuery({
    queryKey: ['manutencoes', propriedadeId],
    queryFn: () => propriedadeId ? agrogestao.entities.ManutencaoAtivo.filter({ propriedade_id: propriedadeId }, '-data_manutencao') : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => agrogestao.entities.AtivoImobilizado.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ativos'] })
  });

  const deleteManutencaoMutation = useMutation({
    mutationFn: (id) => agrogestao.entities.ManutencaoAtivo.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['manutencoes'] })
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  // Filtrar ativos
  const ativosFiltrados = useMemo(() => {
    return ativos.filter(a => {
      const matchSearch = !searchTerm ||
        a.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.marca?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.numero_serie?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.placa?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchCategoria = filterCategoria === "todas" || a.categoria === filterCategoria;
      const matchStatus = filterStatus === "todos" || a.status === filterStatus;

      return matchSearch && matchCategoria && matchStatus;
    });
  }, [ativos, searchTerm, filterCategoria, filterStatus]);

  // Paginação de ativos
  const ativosPaginados = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return ativosFiltrados.slice(start, start + pageSize);
  }, [ativosFiltrados, currentPage, pageSize]);

  // Paginação de manutenções
  const manutencoesPaginadas = useMemo(() => {
    const start = (manutencaoPage - 1) * manutencaoPageSize;
    return manutencoes.slice(start, start + manutencaoPageSize);
  }, [manutencoes, manutencaoPage, manutencaoPageSize]);

  // Reset page quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterCategoria, filterStatus]);

  // Totais
  const totais = useMemo(() => {
    const atv = ativos.filter(a => a.status === 'ativo' || a.status === 'em_manutencao');
    return {
      quantidade: atv.length,
      valorTotal: atv.reduce((a, c) => a + (c.valor_aquisicao || 0), 0),
      emManutencao: ativos.filter(a => a.status === 'em_manutencao').length,
      garantiaVencendo: ativos.filter(a => {
        if (!a.garantia_ate) return false;
        const dias = differenceInDays(parseISO(a.garantia_ate), new Date());
        return dias >= 0 && dias <= 30;
      }).length
    };
  }, [ativos]);

  // Manutenções pendentes
  const manutencoesPendentes = useMemo(() => {
    return manutencoes.filter(m => {
      if (m.status !== 'agendada') return false;
      if (!m.data_proxima) return false;
      const dias = differenceInDays(parseISO(m.data_proxima), new Date());
      return dias <= 30;
    });
  }, [manutencoes]);

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['ativos'] });
    queryClient.invalidateQueries({ queryKey: ['manutencoes'] });
    setDialogOpen(false);
    setManutencaoDialogOpen(false);
    setEditingAtivo(null);
    setEditingManutencao(null);
  };

  const handleVerDetalhes = (ativo) => {
    setSelectedAtivo(ativo);
    setDetalheDialogOpen(true);
  };

  const handleNovaManutencao = (ativo) => {
    setSelectedAtivo(ativo);
    setEditingManutencao(null);
    setManutencaoDialogOpen(true);
  };

  if (!propriedadeId) {
    return <div className="text-center py-20"><p className="text-stone-600">Selecione uma propriedade</p></div>;
  }

  if (isLoading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#40916C] animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-800 flex items-center gap-3">
            <Package className="w-8 h-8 text-[#40916C]" />
            Ativos Imobilizados
          </h1>
          <p className="text-stone-600 mt-1">Gerencie equipamentos, veículos e patrimônio</p>
        </div>

        <div className="flex gap-3">
          <Button onClick={() => { setEditingAtivo(null); setDialogOpen(true); }} className="bg-[#1B4332] hover:bg-[#2D6A4F] gap-2">
            <Plus className="w-4 h-4" />
            Novo Ativo
          </Button>
          <Button variant="outline" onClick={() => { setEditingManutencao(null); setSelectedAtivo(null); setManutencaoDialogOpen(true); }} className="gap-2">
            <Wrench className="w-4 h-4" />
            Nova Manutenção
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <p className="text-blue-100 text-xs">Total de Ativos</p>
            <p className="text-2xl font-bold">{totais.quantidade}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <p className="text-green-100 text-xs">Valor Total</p>
            <p className="text-2xl font-bold">{formatCurrency(totais.valorTotal)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <p className="text-amber-100 text-xs">Em Manutenção</p>
            <p className="text-2xl font-bold">{totais.emManutencao}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <p className="text-purple-100 text-xs">Garantia Vencendo</p>
            <p className="text-2xl font-bold">{totais.garantiaVencendo}</p>
          </CardContent>
        </Card>
      </div>

      {/* Alertas */}
      {(manutencoesPendentes.length > 0 || totais.garantiaVencendo > 0) && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-700 mb-2">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-semibold">Alertas</span>
            </div>
            <div className="space-y-1 text-sm text-amber-800">
              {manutencoesPendentes.length > 0 && (
                <p>• {manutencoesPendentes.length} manutenção(ões) programada(s) para os próximos 30 dias</p>
              )}
              {totais.garantiaVencendo > 0 && (
                <p>• {totais.garantiaVencendo} ativo(s) com garantia vencendo em 30 dias</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-stone-100 p-1">
          <TabsTrigger value="inventario" className="gap-2"><Package className="w-4 h-4" />Inventário</TabsTrigger>
          <TabsTrigger value="manutencoes" className="gap-2"><Wrench className="w-4 h-4" />Manutenções</TabsTrigger>
          <TabsTrigger value="depreciacao" className="gap-2"><TrendingDown className="w-4 h-4" />Depreciação</TabsTrigger>
        </TabsList>

        {/* Inventário */}
        <TabsContent value="inventario" className="mt-6 space-y-4">
          {/* Filtros */}
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input placeholder="Buscar por nome, marca, série, placa..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
            </div>
            <Select value={filterCategoria} onValueChange={setFilterCategoria}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Categoria" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                {categorias.map(c => <SelectItem key={c.value} value={c.value}>{c.icon} {c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {Object.entries(statusLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Lista */}
          {ativosFiltrados.length === 0 ? (
            <Card className="p-12 text-center">
              <Package className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">Nenhum ativo encontrado</p>
            </Card>
          ) : (
            <>
              <div className="text-sm text-stone-500 mb-2">{ativosFiltrados.length} ativo(s) encontrado(s)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <AnimatePresence>
                  {ativosPaginados.map((ativo, idx) => (
                    <motion.div key={ativo.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                      <Card className="bg-white border-stone-200 hover:shadow-lg transition-all overflow-hidden">
                        {ativo.foto_url && (
                          <div className="h-32 bg-stone-100">
                            <img src={ativo.foto_url} alt={ativo.nome} className="w-full h-full object-cover" />
                          </div>
                        )}
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">{categoriaLabels[ativo.categoria]?.icon}</span>
                              <div>
                                <h3 className="font-semibold text-stone-800">{ativo.nome}</h3>
                                <p className="text-xs text-stone-500">{ativo.marca} {ativo.modelo}</p>
                              </div>
                            </div>
                            <Badge className={statusLabels[ativo.status]?.color}>{statusLabels[ativo.status]?.label}</Badge>
                          </div>

                          <div className="space-y-1 text-sm text-stone-600 mb-3">
                            {ativo.numero_serie && <p>Série: {ativo.numero_serie}</p>}
                            {ativo.placa && <p>Placa: {ativo.placa}</p>}
                            <p className="font-semibold text-green-600">{formatCurrency(ativo.valor_aquisicao)}</p>
                          </div>

                          <div className="flex gap-2 pt-3 border-t border-stone-100">
                            <Button variant="outline" size="sm" className="flex-1" onClick={() => handleVerDetalhes(ativo)}>
                              <Eye className="w-4 h-4 mr-1" />Detalhes
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleNovaManutencao(ativo)}>
                              <Wrench className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => { setEditingAtivo(ativo); setDialogOpen(true); }}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteMutation.mutate(ativo.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
              <Paginacao
                totalItems={ativosFiltrados.length}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                pageSize={pageSize}
                onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
                pageSizeOptions={[6, 12, 24, 48]}
              />
            </>
          )}
        </TabsContent>

        {/* Manutenções */}
        <TabsContent value="manutencoes" className="mt-6">
          <Card>
            <CardContent className="p-0">
              {manutencoes.length === 0 ? (
                <div className="p-12 text-center">
                  <Wrench className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500">Nenhuma manutenção registrada</p>
                </div>
              ) : (
                <>
                  <div className="p-4 border-b border-stone-100 text-sm text-stone-500">{manutencoes.length} manutenção(ões) registrada(s)</div>
                  <div className="divide-y divide-stone-100">
                    {manutencoesPaginadas.map(m => {
                      const ativo = ativos.find(a => a.id === m.ativo_id);
                      return (
                        <div key={m.id} className="p-4 hover:bg-stone-50 flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${m.tipo === 'preventiva' ? 'bg-blue-100' : m.tipo === 'corretiva' ? 'bg-amber-100' : 'bg-purple-100'}`}>
                            <Wrench className={`w-5 h-5 ${m.tipo === 'preventiva' ? 'text-blue-600' : m.tipo === 'corretiva' ? 'text-amber-600' : 'text-purple-600'}`} />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-stone-800">{m.descricao}</p>
                            <p className="text-sm text-stone-500">{ativo?.nome} • {format(parseISO(m.data_manutencao), "dd/MM/yyyy")}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-stone-800">{formatCurrency(m.custo_total)}</p>
                            <Badge variant="outline">{m.tipo}</Badge>
                          </div>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteManutencaoMutation.mutate(m.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                  <Paginacao
                    totalItems={manutencoes.length}
                    currentPage={manutencaoPage}
                    onPageChange={setManutencaoPage}
                    pageSize={manutencaoPageSize}
                    onPageSizeChange={(size) => { setManutencaoPageSize(size); setManutencaoPage(1); }}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Depreciação */}
        <TabsContent value="depreciacao" className="mt-6">
          <RelatorioDepreciacao ativos={ativos} />
        </TabsContent>
      </Tabs>

      {/* Dialog Ativo */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAtivo ? "Editar Ativo" : "Novo Ativo"}</DialogTitle>
            <DialogDescription>
              {editingAtivo ? "Atualize os dados do ativo imobilizado" : "Cadastre um novo ativo imobilizado"}
            </DialogDescription>
          </DialogHeader>
          <AtivoForm ativo={editingAtivo} onSuccess={handleSuccess} onCancel={() => setDialogOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Dialog Manutenção */}
      <Dialog open={manutencaoDialogOpen} onOpenChange={setManutencaoDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingManutencao ? "Editar Manutenção" : "Nova Manutenção"}</DialogTitle>
            <DialogDescription>
              {editingManutencao ? "Atualize os dados da manutenção" : "Cadastre uma nova manutenção do ativo"}
            </DialogDescription>
          </DialogHeader>
          <ManutencaoForm
            manutencao={editingManutencao}
            ativoId={selectedAtivo?.id}
            ativos={ativos}
            onSuccess={handleSuccess}
            onCancel={() => setManutencaoDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={detalheDialogOpen} onOpenChange={setDetalheDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">{categoriaLabels[selectedAtivo?.categoria]?.icon}</span>
              {selectedAtivo?.nome}
            </DialogTitle>
            <DialogDescription>
              Detalhes do ativo imobilizado
            </DialogDescription>
          </DialogHeader>
          {selectedAtivo && (
            <div className="space-y-4">
              {/* Foto / Placeholder visual */}
              {selectedAtivo.foto_url ? (
                <img src={selectedAtivo.foto_url} alt={selectedAtivo.nome} className="w-full h-52 object-cover rounded-xl" />
              ) : (
                <div className="w-full h-36 rounded-xl bg-gradient-to-br from-stone-100 to-stone-200 flex flex-col items-center justify-center gap-2">
                  <span className="text-5xl">{categoriaLabels[selectedAtivo.categoria]?.icon}</span>
                  <span className="text-xs text-stone-400">Sem foto cadastrada</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-stone-500">Marca/Modelo:</span><p className="font-medium">{selectedAtivo.marca} {selectedAtivo.modelo}</p></div>
                <div><span className="text-stone-500">Nº Série:</span><p className="font-medium">{selectedAtivo.numero_serie || '-'}</p></div>
                <div><span className="text-stone-500">Data Aquisição:</span><p className="font-medium">{selectedAtivo.data_aquisicao ? format(parseISO(selectedAtivo.data_aquisicao), "dd/MM/yyyy") : '-'}</p></div>
                <div><span className="text-stone-500">Valor:</span><p className="font-medium text-green-600">{formatCurrency(selectedAtivo.valor_aquisicao)}</p></div>
                <div><span className="text-stone-500">Localização:</span><p className="font-medium">{selectedAtivo.localizacao || '-'}</p></div>
                <div><span className="text-stone-500">Responsável:</span><p className="font-medium">{selectedAtivo.responsavel || '-'}</p></div>
                {selectedAtivo.placa && <div><span className="text-stone-500">Placa:</span><p className="font-medium">{selectedAtivo.placa}</p></div>}
                {selectedAtivo.garantia_ate && <div><span className="text-stone-500">Garantia até:</span><p className="font-medium">{format(parseISO(selectedAtivo.garantia_ate), "dd/MM/yyyy")}</p></div>}
              </div>
              {selectedAtivo.observacoes && (
                <div><span className="text-stone-500 text-sm">Observações:</span><p className="text-sm">{selectedAtivo.observacoes}</p></div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
